import {
  ArcElement,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  Tooltip,
  type TooltipItem,
} from 'chart.js';
import {useRouter} from 'next/router';
import {useState} from 'react';
import {Pie} from 'react-chartjs-2';
import type {z} from 'zod';
import {
  COLOR_BLACK_3,
  COLOR_PIE_GRAPH_1,
  COLOR_PIE_GRAPH_2,
  COLOR_PIE_GRAPH_3,
  COLOR_PIE_GRAPH_4,
} from '../../../../../config/constants/colorCodes';
import {HIDE_OFFERED_ATTRIBUTE_COMPANY_IDS} from '../../../../../core/entities/company/constants';
import {
  type InternCompanies,
  type Sex,
  type Toeic,
  Universities as UniversitiesSchema,
} from '../../../../../core/entities/companyDetail/schema';
import {BarChart} from './BarChart';
import {SectionTitle} from './SectionTitle';

// パーセント表示用プラグインの定義
const percentagePlugin = {
  id: 'percentage',
  afterDatasetsDraw(chart: ChartJS<'pie', number[], string>) {
    const {ctx} = chart;
    for (const [i, dataset] of chart.data.datasets.entries()) {
      const meta = chart.getDatasetMeta(i);
      if (!meta.hidden) {
        for (const [index, element] of meta.data.entries()) {
          // データ値と計算
          const data = dataset.data[index];
          const total = dataset.data.reduce((acc, val) => acc + val, 0);
          const percentageValue = (data! / total) * 100;
          const percentage = `${percentageValue.toFixed(1)}%`;

          // 10%以上のデータのみ円グラフに表示
          if (percentageValue > 13) {
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const position = element.tooltipPosition(true);
            ctx.fillText(percentage, position.x, position.y);
          }
        }
      }
    }
  },
};

ChartJS.register(ArcElement, Tooltip, Legend, percentagePlugin);

type Props = {
  sex: z.infer<typeof Sex>;
  universities: z.infer<typeof UniversitiesSchema>;
  toeic: z.infer<typeof Toeic>;
  internCompanies: z.infer<typeof InternCompanies>;
  companyID: number;
};

// 内定者の属性
export type SexCount = {
  sex: string; // 男性 or 女性
  percent: number;
};

export type BarChartData = {
  name?: string;
  percent?: number;
};

type ToeicScoreRangeCount = {
  scoreRange: string; // TOEICスコア帯
  percent?: number;
};

// TOEIC の各スコア帯の定数
const TOEIC_SCORE_RANGES_1 = '700以下';
const TOEIC_SCORE_RANGES_2 = '701 ~ 800';
const TOEIC_SCORE_RANGES_3 = '801 ~ 900';
const TOEIC_SCORE_RANGES_4 = '901以上';

export const sexBgColors = (sexCount: SexCount[]) => {
  return sexCount
    .filter((data) => data.sex === '男性' || data.sex === '女性')
    .map((data) => {
      switch (data.sex) {
        case '男性': {
          return COLOR_PIE_GRAPH_1;
        }

        case '女性': {
          return COLOR_BLACK_3;
        }

        default: {
          return '';
        }
      }
    });
};

const isValidScoreRange = (scoreRange: string): boolean => {
  switch (scoreRange) {
    case TOEIC_SCORE_RANGES_1:
    case TOEIC_SCORE_RANGES_2:
    case TOEIC_SCORE_RANGES_3:
    case TOEIC_SCORE_RANGES_4: {
      return true;
    }

    default: {
      return false;
    }
  }
};

export const convertUniversitiesData = (
  universities: z.infer<typeof UniversitiesSchema>,
): BarChartData[] => {
  return UniversitiesSchema.parse(universities)
    .map((university, index) =>
      index < 10
        ? {
            name: university.name ?? '',
            percent: university.count ?? 0,
          }
        : null,
    )
    .filter((item) => item !== null);
};

export const convertSexData = (sex: z.infer<typeof Sex>): SexCount[] => {
  return [
    {
      sex: '男性',
      percent: sex.man ?? 0,
    },
    {
      sex: '女性',
      percent: sex.woman ?? 0,
    },
  ];
};

const convertInternCompaniesData = (
  internCompanies: z.infer<typeof InternCompanies>,
): BarChartData[] => {
  return internCompanies
    .map((company, index) =>
      index < 10
        ? {
            name: company.name ?? '',
            percent: company.count ?? 0,
          }
        : null,
    )
    .filter((item) => item !== null);
};

const convertToeicData = (
  toeic: z.infer<typeof Toeic>,
): ToeicScoreRangeCount[] => {
  return [
    {
      scoreRange: TOEIC_SCORE_RANGES_1,
      percent: toeic.under700,
    },
    {
      scoreRange: TOEIC_SCORE_RANGES_2,
      percent: toeic['701to800'],
    },
    {
      scoreRange: TOEIC_SCORE_RANGES_3,
      percent: toeic['801to900'],
    },
    {
      scoreRange: TOEIC_SCORE_RANGES_4,
      percent: toeic.over901,
    },
  ];
};

// 内定者が5人以下なら表示しない
export const OfferedAttribute: React.FC<Props> = ({
  sex,
  universities,
  toeic,
  internCompanies,
  companyID,
}) => {
  const sexCount = convertSexData(sex);
  const univCount = convertUniversitiesData(universities);
  const toeicScoreRangeCount = convertToeicData(toeic);
  const internCompanyCount = convertInternCompaniesData(internCompanies);
  // 大学のPieグラフのラベルのページネーション
  const [univCountPage, _setUnivCountPage] = useState(0);

  const router = useRouter();
  const {id} = router.query;

  // 性別のデータを処理
  const sexData = {
    labels: sexCount.map((data) => data.sex),
    datasets: [
      {
        data: sexCount.map((data) => data.percent),
        backgroundColor: sexBgColors(sexCount),
      },
    ],
  };

  // TOEICスコア帯のデータを処理
  const toeicBgColors = toeicScoreRangeCount
    .filter((data) => isValidScoreRange(data.scoreRange))
    .map((data) => {
      switch (data.scoreRange) {
        case TOEIC_SCORE_RANGES_1: {
          return COLOR_PIE_GRAPH_1;
        }

        case TOEIC_SCORE_RANGES_2: {
          return COLOR_PIE_GRAPH_2;
        }

        case TOEIC_SCORE_RANGES_3: {
          return COLOR_PIE_GRAPH_3;
        }

        case TOEIC_SCORE_RANGES_4: {
          return COLOR_PIE_GRAPH_4;
        }

        default: {
          return '';
        }
      }
    });

  const toeicData = {
    labels: toeicScoreRangeCount
      .filter((data) => data.percent !== undefined)
      .map((data) => data.scoreRange),
    datasets: [
      {
        data: toeicScoreRangeCount
          .filter((data) => data.percent !== undefined)
          .map((data) => data.percent),
        backgroundColor: toeicBgColors,
      },
    ],
  };

  const options: ChartOptions<'pie'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          filter(item, chart) {
            // 複数ページの場合現在開いているデータのみ表示
            if (
              chart.datasets[0] !== undefined &&
              chart.datasets[0].data.length > 5
            ) {
              return (
                item.index! >= univCountPage * 5 &&
                item.index! < (univCountPage + 1) * 5
              );
            }

            return true;
          },
        },
      },
      tooltip: {
        callbacks: {
          label(tooltipItem: TooltipItem<'pie'>) {
            // グラフのパーセント表示
            const total = tooltipItem.dataset.data.reduce(
              (acc, val) => acc + val,
              0,
            );
            const percentage = `${(((tooltipItem.raw as number) / total) * 100).toFixed(1)}%`;
            return percentage;
          },
        },
      },
    },
  };

  if (HIDE_OFFERED_ATTRIBUTE_COMPANY_IDS.includes(Number(id))) {
    return null;
  }

  return (
    <div className="mt-8 pc:mt-16">
      <SectionTitle
        id="offered_users_attribute"
        sectionTitle="内定者属性（外資就活ドットコム会員の過去データ累積値）"
        companyID={companyID}
        showEntrysheetAndReport={true}
      />
      <div className="rounded-b-[12px] rounded-t-[4px] border-t-[1px] border-solid border-t-line-1 pc:border-t-[0px]">
        <div className="pc:mt-8 bg-white p-16 pc:flex pc:gap-8">
          <div className="pc:w-[50%]">
            <h3 className="font-bold pc:p-8">
              採用大学(会員学生の内定先申告データ参照)
            </h3>
            <BarChart data={univCount} />
          </div>
          <div className="pc:w-[50%] mt-16 pc:mt-0">
            <h3 className="font-bold pc:p-8">インターン先（上位10社）</h3>
            <BarChart data={internCompanyCount} />
          </div>
        </div>
        <div className="pc:mt-8 pc:px-[128px] pc:flex pc:justify-between items-center bg-white p-16 rounded-t-[4px] rounded-b-[12px]">
          <div className="mt-16 pc:mt-0">
            <h3 className="font-bold p-8">TOEIC</h3>
            <Pie data={toeicData} options={options} />
          </div>
          <div className="mt-16 pc:mt-0">
            <h3 className="font-bold p-8">性別</h3>
            <Pie data={sexData} options={options} />
          </div>
        </div>
        <p className="text-sm text-left mt-8">
          ※外資就活ドットコム会員の過去データ累積値
        </p>
      </div>
    </div>
  );
};
